import {
  EMPTY_LANDING,
  EMPTY_TREASURY,
  type AlertItem,
  type LandingStats,
  type Treasury,
} from '@afterhours/core/published';
import { explorerAddressUrl } from '@afterhours/core/treasury';
import { Link } from 'react-router-dom';
import { DataAge } from '../components/DataAge';
import { SiteFooter, SiteHeader } from '../components/SiteChrome';
import { useSnapshot } from '../data/useSnapshot';
import { API_LIVE, BRAND, priceLabel } from '../lib/brand';
import { count, hhmmUtc, usd, usdCompact } from '../lib/format';

/**
 * The landing page, ported from `apps/web/app/page.tsx`.
 *
 * Same markup and the same rule about numbers: nothing here is estimated, and
 * a figure with no stored row behind it renders as a designed empty state
 * rather than a zero. The only structural change is where the data comes from.
 * `apps/web` awaits three database queries on the server; this reads the
 * published snapshot that was built from those same queries, so the figures
 * are identical and can only differ by age — which `<DataAge/>` states.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** The mockup's five allocation shades, darkest last; cycled if more legs. */
const ALLOC_COLORS = ['#B6FF3C', '#84B82C', '#4E7A1C', '#2E4A12', '#1C2427'];

const KIND_CLASS: Record<string, string> = {
  gap: 'k-gap',
  dislocation: 'k-dis',
  whale: 'k-whale',
  launch: 'k-meme',
};

const KIND_LABEL: Record<string, string> = {
  gap: 'Gap',
  dislocation: 'Disloc',
  whale: 'Whale',
  launch: 'Launch',
};

/**
 * Time to the next Sunday 00:00 UTC. This is a schedule fact, not a claim about
 * stored data, so it renders even before the first sweep exists.
 */
function nextSweep(now: number): string {
  const d = new Date(now);
  const daysAhead = (7 - d.getUTCDay()) % 7 || 7;
  const target = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() + daysAhead,
    0,
    0,
    0,
    0,
  );
  const left = target - now;
  const days = Math.floor(left / DAY_MS);
  const hours = Math.floor((left % DAY_MS) / 3_600_000);
  return `Sun 00:00 UTC · in ${days}d ${hours}h`;
}

/** "6d ago" / "4h ago" / "just now" for the wallet proof line. */
function ago(ts: number, now: number): string {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 90) return 'just now';
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function AlertRow({ a }: { a: AlertItem }) {
  return (
    <div className="row">
      <span className={`kd ${KIND_CLASS[a.type] ?? 'k-none'}`}>
        {KIND_LABEL[a.type] ?? a.type}
      </span>
      <span className="tx">
        {a.symbol ? <b>{a.symbol.toUpperCase()}</b> : null} {a.headline}
      </span>
      <span className="ts">{hhmmUtc(a.ts)}</span>
    </div>
  );
}

export function Landing() {
  const { snapshot, now } = useSnapshot();

  // No snapshot is the same situation as an empty database: render the empty
  // states rather than a spinner, so the page is never blank and the strip
  // above says why the numbers are missing.
  const stats: LandingStats = snapshot?.landing ?? EMPTY_LANDING;
  const tre: Treasury = snapshot?.treasury ?? EMPTY_TREASURY;
  const alerts: AlertItem[] = (snapshot?.latestAlerts ?? []).slice(0, 5);
  const live = alerts.length > 0 && now - alerts[0]!.ts < DAY_MS;

  return (
    <div className="page">
      <div className="grid" />

      <SiteHeader active="alerts" />

      <div className="datastrip">
        <DataAge />
      </div>

      <div className="body">
        <div className="col-l">
          <div className="eyebrow">
            <span className="line" />
            {BRAND.eyebrow}
          </div>

          <h1>
            Wall Street sleeps.
            <br />
            <span className="green">On-chain doesn&rsquo;t.</span>
          </h1>

          <p className="lede">
            An agent watches every tokenized stock on {BRAND.chain} around the clock and posts the
            overnight gaps, price dislocations, whale flows and new stock-paired launches —{' '}
            <b>free on Telegram, the whole book in the terminal.</b>
          </p>

          {/* Alerts post to Telegram only since 29 Sep; X carries announcements. */}
          <div className="ctas">
            <a className="btn primary" href={BRAND.links.telegram} rel="noreferrer noopener" target="_blank">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M14.6 2.2L1.9 7.1c-.6.2-.6 1.1 0 1.3l3.1 1 1.2 3.8c.2.5.8.6 1.1.2l1.7-1.7 3.2 2.4c.4.3 1 .1 1.1-.4l2.1-10.4c.2-.7-.4-1.2-.8-1.1zM6.2 9.6l6.1-5.2-4.6 6.1-.2 2-1.3-2.9z"
                  fill="currentColor"
                />
              </svg>
              Get alerts on Telegram
            </a>
            <Link className="btn" to="/terminal">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect
                  x="1.8"
                  y="2.6"
                  width="12.4"
                  height="10.8"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M4.6 6.4l2 1.9-2 1.9M8.4 10.6h3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Open terminal
            </Link>
            {/*
              The token is not live yet, so there is no Pons URL in brand.json.
              A dead href or a link to the launchpad's front page would both
              imply the token is buyable; this says plainly that it is not.
            */}
            {BRAND.links.pons ? (
              <a className="btn ghost" href={BRAND.links.pons} rel="noreferrer noopener" target="_blank">
                Buy {BRAND.ticker} on {BRAND.launchpad}
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M4 12L12 4M6 4h6v6"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            ) : (
              <span className="btn ghost" aria-disabled="true">
                {BRAND.ticker} not live on {BRAND.launchpad} yet
              </span>
            )}
          </div>
          {BRAND.links.pons ? <p className="token-risk">{BRAND.tokenRisk}</p> : null}

          <div className="tiles">
            <div className="tile">
              {/*
                Signals, not posts: nothing is published until the poster goes
                live, and "posted" about a draft is a claim that did not happen.
              */}
              <div className="k">Signals raised</div>
              <div className="v">{count(stats.signalsRaised ?? null)}</div>
              <div className="s">
                {stats.signalsRaised24h == null ? (
                  // An older publisher does not send the field; "none" would be false.
                  'Not in this snapshot yet'
                ) : stats.signalsRaised24h > 0 ? (
                  <>
                    <span className="up">+{count(stats.signalsRaised24h)}</span> in the last 24h
                  </>
                ) : (
                  'None in the last 24h'
                )}
              </div>
            </div>
            <div className="tile">
              <div className="k">Stock tokens tracked</div>
              <div className="v">{count(stats.stockTokens)}</div>
              <div className="s">Stock tokens indexed on {BRAND.chain}</div>
            </div>
            <div className="tile">
              <div className="k">Treasury in stock tokens</div>
              <div className="v acid">{usd(stats.treasuryUsd, 0)}</div>
              <div className="s">
                {stats.sweptThisWeekUsd ? (
                  <>
                    <span className="up">+{usdCompact(stats.sweptThisWeekUsd)}</span> routed this
                    week
                  </>
                ) : (
                  'No sweep recorded yet'
                )}
              </div>
            </div>
          </div>

          <div className="section-h">
            <h2>How {BRAND.ticker} works</h2>
            <span className="rule" />
            <span className="tag">The token buys stocks</span>
          </div>

          <div className="steps">
            <div className="step">
              <div className="n">
                <span className="sq">1</span>Collect
              </div>
              <h3>{BRAND.launchpad} creator fees accrue</h3>
              <p>
                Every trade of {BRAND.ticker} on {BRAND.launchpad} pays a creator fee to the project
                wallet. <b>No team allocation, no presale.</b>
              </p>
            </div>
            <div className="step">
              <div className="n">
                <span className="sq">2</span>Route
              </div>
              <h3>Swept weekly into stock tokens</h3>
              <p>
                Sundays at 00:00 UTC the balance buys <b>stock tokens</b> on-chain, sized by a fixed
                rule published before the sweep.
              </p>
            </div>
            <div className="step">
              <div className="n">
                <span className="sq">3</span>Prove
              </div>
              <h3>Holdings published, wallet public</h3>
              <p>
                Every buy is one on-chain tx. <b>Wallet, holdings and cost basis</b> live on the{' '}
                <Link to="/honest-numbers">Honest numbers</Link> page.
              </p>
            </div>
          </div>
        </div>

        <div className="col-r">
          <div className="card">
            <div className="card-h">
              <span className="t">Treasury allocation</span>
              <span className="v">{usd(tre.total, 0)}</span>
            </div>
            {tre.allocations.length > 0 ? (
              <>
                <div className="allocbar">
                  {tre.allocations.map((a, i) => (
                    <span
                      key={a.symbol}
                      style={{
                        width: `${(a.share * 100).toFixed(1)}%`,
                        background: ALLOC_COLORS[i % ALLOC_COLORS.length],
                      }}
                    />
                  ))}
                </div>
                <div className="alloclegend">
                  {tre.allocations.map((a, i) => (
                    <span className="lg" key={a.symbol}>
                      <i style={{ background: ALLOC_COLORS[i % ALLOC_COLORS.length] }} />
                      {a.symbol.toUpperCase()} <b>{Math.round(a.share * 100)}%</b>
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="allocbar empty" />
                <div className="empty">
                  <b>No sweep recorded yet</b>
                  The bar fills from the <span className="mono">sweeps</span> table once the first
                  weekly buy is recorded with its tx hash. Nothing is estimated here.
                </div>
              </>
            )}
            <div className="alloc-foot">
              <span className="l">Next sweep</span>
              <span className="r">{nextSweep(now)}</span>
            </div>
          </div>

          <div className="proof">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
              <circle cx="13" cy="13" r="11" stroke="#B6FF3C" strokeWidth="1.5" opacity=".55" />
              <path
                d="M8.2 13.4l3.2 3.2 6.4-6.8"
                stroke="#B6FF3C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="txt">
              <div className="a">
                The treasury wallet is public. <b>Verify every buy yourself.</b>
              </div>
              <div className="b">
                {BRAND.treasuryAddress ? (
                  <>
                    <a
                      href={explorerAddressUrl(BRAND.treasuryAddress)}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {BRAND.treasuryAddress}
                    </a>
                    {` · ${tre.sweepCount} sweeps${
                      tre.lastSweepTs ? ` · last: ${ago(tre.lastSweepTs, now)}` : ''
                    } · `}
                    <Link to="/honest-numbers#treasury">full ledger</Link>
                  </>
                ) : (
                  'Address published before the first sweep'
                )}
              </div>
            </div>
          </div>

          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="card-h">
              <span className="t">Latest alerts</span>
              <span className="v" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span className={live ? 'dot' : 'dot off'} />
                {live ? 'LIVE' : 'IDLE'}
              </span>
            </div>
            {alerts.length > 0 ? (
              <div className="mini">
                {alerts.map((a) => (
                  <AlertRow a={a} key={a.id} />
                ))}
              </div>
            ) : (
              <div className="empty">
                <b>No alerts posted yet</b>
                The agent is scanning. A row appears here the moment a gap, dislocation, whale flow
                or stock-paired launch clears its configured threshold.
              </div>
            )}
          </div>

          <div className="card" style={{ padding: '15px 20px' }}>
            <div className="card-h">
              <span className="t">Terminal &amp; API</span>
              <span className="v">{priceLabel}</span>
            </div>
            <div
              style={{
                marginTop: 11,
                fontSize: 12,
                lineHeight: 1.5,
                color: 'var(--text-3)',
              }}
            >
              {API_LIVE
                ? 'No seats, no subscription. Pay per call with a wallet — the agent settles micropayments on-chain and streams you the full book.'
                : 'Coming soon: pay per call with a wallet, no seats, no subscription. The paid endpoint is not live yet; the free terminal is.'}
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
